import axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from "axios";
import {
  IAppointment,
  IAppointmentDetail,
} from "../../types/appointments/Appointment";
import { IAssociation } from "../../types/association/Association";
import { IAvailability } from "../../types/availabilities/Availabilities";
import { ICompetition } from "../../types/competition/Competition";
import { IGame } from "../../types/game/Game";
import { IGameConfiguration } from "../../types/game/GameConfiguration";
import { IPerson } from "../../types/persons/person";
import { IPost } from "../../types/post/Post";
import { IShift, IShiftSession } from "../../types/shifts/Shift";
import { ITeam } from "../../types/team/Team";
import { IVenue } from "../../types/venue/Venue";

export interface GenericTransport<T> {
  success?: boolean;
  data: T;
  error?: string;
  warnings?: string[];
  total?: number;

  availabilities?: IAvailability[];
  associations?: IAssociation[];
  persons?: IPerson[];
  posts?: IPost[];
  shifts?: IShift[];
  shiftSessions?: IShiftSession[];
  games?: IGame[];
  gameConfigs: IGameConfiguration[];
  teams?: ITeam[];
  competitions?: ICompetition[];
  venues?: IVenue[];
  appointments: IAppointment<IAppointmentDetail>[];
  // gameConfigs?: IGame[]
}

class NetworkError extends Error {
  constructor(message: string, additionalData?: any) {
    super(message);
    this.name = 'NetworkError';

    // Safely add additional properties if provided
    if (additionalData && typeof additionalData === 'object') {
      const keys = Object.keys(additionalData);
      keys.forEach((k) => {
        if (k !== "message") {
          try {
            (this as any)[k] = additionalData[k];
          } catch (err) {
            // Ignore if property can't be set
          }
        }
      });
    }
  }
}

/**
 * Define some behaviour that all classes could take advantage of
 */
export class ApiBase {
  static prepareQueryParams(
    queryParams: { [key: string]: any },
    removeEmptyValues: boolean = true,
  ): string {
    const keysToCheck = removeEmptyValues
      ? Object.keys(queryParams).filter((q) => !!queryParams[q])
      : Object.keys(queryParams);
    if (keysToCheck.length === 0) return "";
    return `?${keysToCheck
      .map((q) => `${q}=` + `${queryParams[q]}`)
      .join("&")}`;
  }

  static genericThenBlock<T>(
    response: AxiosResponse<GenericTransport<T>>,
  ): AxiosResponse<GenericTransport<T>> {
    if (!response.data.success) {
      const error = response.data.error as any;
      let errorMessage = "An unknown error has occurred";

      if (typeof error === 'string') {
        errorMessage = error;
      } else if (error && typeof error === 'object') {
        // Handle nested error objects like { message: "...", code: "..." }
        errorMessage = error.message || error.error || errorMessage;
      }

      throw new Error(errorMessage);
    }
    return response;
  }

  static errorBlock(error: AxiosError) {
    if (error.response && error.response.data) {
      const responseData = error.response.data as any;
      let errorMessage = `HTTP ${error.response.status}: ${error.response.statusText}`;

      // Handle different error response formats
      if (typeof responseData.error === 'string') {
        errorMessage = responseData.error;
      } else if (responseData.error && typeof responseData.error === 'object') {
        // Handle nested error objects like { message: "...", code: "..." }
        errorMessage = responseData.error.message || responseData.error.error || errorMessage;
      } else if (responseData.message) {
        errorMessage = responseData.message;
      }

      throw new NetworkError(errorMessage, error);
    }

    if (error.message) {
      throw new NetworkError(error.message, error);
    }

    throw new NetworkError("An unknown network error occurred", error);
  }

  static async get<T>(
    url: string,
    config?: AxiosRequestConfig,
  ): Promise<AxiosResponse<GenericTransport<T>>> {
    try {
      return await axios
        .get<GenericTransport<T>>(url, config)
        .then(ApiBase.genericThenBlock);
    } catch (er: any) {
      throw ApiBase.errorBlock(er);
    }
  }

  static async post<T>(
    url: string,
    data?: any,
    config?: AxiosRequestConfig,
  ): Promise<AxiosResponse<GenericTransport<T>>> {
    try {
      return await axios
        .post<GenericTransport<T>>(url, data, config)
        .then((r) => ApiBase.genericThenBlock<T>(r));
    } catch (er: any) {
      throw ApiBase.errorBlock(er);
    }
  }

  static async patch<T>(
    url: string,
    data?: any,
    config?: AxiosRequestConfig,
  ): Promise<AxiosResponse<GenericTransport<T>>> {
    try {
      return await axios
        .patch<GenericTransport<T>>(url, data, config)
        .then((r) => ApiBase.genericThenBlock<T>(r));
    } catch (er: any) {
      throw ApiBase.errorBlock(er);
    }
  }

  static async put<T>(
    url: string,
    data?: any,
    config?: AxiosRequestConfig,
  ): Promise<AxiosResponse<GenericTransport<T>>> {
    try {
      return await axios
        .put<GenericTransport<T>>(url, data, config)
        .then((r) => ApiBase.genericThenBlock<T>(r));
    } catch (er: any) {
      throw ApiBase.errorBlock(er);
    }
  }

  static async delete<T>(
    url: string,
    data?: any,
    config?: AxiosRequestConfig,
  ): Promise<AxiosResponse<GenericTransport<T>>> {
    try {
      return await axios
        .delete<GenericTransport<T>>(url, config)
        .then((r) => ApiBase.genericThenBlock<T>(r));
    } catch (er: any) {
      throw ApiBase.errorBlock(er);
    }
  }

  /**
   * Generic API call helper with consistent error handling and validation
   */
  static async genericApiCall<T>(
    method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE',
    url: string,
    data?: any,
    errorMessage?: string,
    config?: AxiosRequestConfig,
  ): Promise<T> {
    try {
      let response: AxiosResponse<GenericTransport<T>>;

      switch (method) {
        case 'GET':
          response = await this.get<T>(url, config);
          break;
        case 'POST':
          response = await this.post<T>(url, data, config);
          break;
        case 'PUT':
          response = await this.put<T>(url, data, config);
          break;
        case 'PATCH':
          response = await this.patch<T>(url, data, config);
          break;
        case 'DELETE':
          response = await this.delete<T>(url, data, config);
          break;
        default:
          throw new Error(`Unsupported HTTP method: ${method}`);
      }

      return response.data.data;
    } catch (error) {
      const message = errorMessage || `Failed to ${method.toLowerCase()} data`;
      console.error(message, error);
      throw error;
    }
  }

  /**
   * Validates MongoDB ObjectId format
   */
  static validateObjectId(id: string, fieldName: string = 'ID'): void {
    if (!id || typeof id !== 'string' || !/^[a-fA-F0-9]{24}$/.test(id)) {
      throw new Error(`Invalid ${fieldName} format`);
    }
  }
}
